/**
 * Policy engine — `compile`, `decide`, `grant` (see SUP/policy-engine-design).
 *
 * Three bare verbs, each returning its noun: `compile` → CompiledPolicy,
 * `decide` → Decision, `grant` → PolicyRule. The hot path (`decide`) is pure:
 * a function of (compiled policy, request, now) with no I/O, no clock reads, no
 * ambient state — so it runs identically in the DO, the browser, and tests.
 *
 * Design invariants enforced here:
 * - Canonicalize, then match: a request ref is normalized + validated before
 *   any rule sees it; failure is a structural deny no rule can override.
 * - Most-restrictive-wins (`deny > escalate > allow`) at every combination
 *   point — per-principal across matched rules, and across the chain.
 * - Default deny: a principal matched by no rule denies.
 * - Chain = intersection: the decision is the most restrictive across chain
 *   principals, so neither an app nor its driver can escalate through the other.
 */
import { invariant } from "ts-invariant";

import type {
  Decision,
  Effect,
  GroupSelector,
  PolicyDomain,
  PolicyRequest,
  PolicyRule,
  PolicySet,
  Principal,
  PrincipalSelector,
  PrincipalVerdict,
  GrantTemplate,
  RuleCondition,
} from "./types";

/** Restrictiveness rank; higher wins a most-restrictive-wins combination. */
const RANK: Record<Effect, number> = { allow: 0, escalate: 1, deny: 2 };
const BY_RANK: Effect[] = ["allow", "escalate", "deny"];

/** A rule with its patterns/actions resolved at compile time. */
interface CompiledRule {
  id: string;
  selectors: readonly PrincipalSelector[];
  /** Canonical patterns (via the domain's `canonicalizePattern`). */
  patterns: readonly string[];
  /** `"*"` (any action) or the lattice-expanded set of primitive actions. */
  actionMatch: "*" | ReadonlySet<string>;
  effect: Effect;
  expiresAt?: number;
  when?: RuleCondition;
}

declare const compiled: unique symbol;

/**
 * An immutable, pre-validated policy — the input to `decide`. Its internals
 * (domain map, per-domain rule index) are `@internal`: callers treat it as
 * opaque and rebuild it when rules change (cheap; policy sets are small). The
 * brand keeps construction inside {@link compile} — there is no other way to
 * mint one, so a `CompiledPolicy` always went through rule validation.
 */
export interface CompiledPolicy {
  /** @internal */ readonly domains: ReadonlyMap<string, PolicyDomain>;
  /** @internal */ readonly rulesByDomain: ReadonlyMap<
    string,
    readonly CompiledRule[]
  >;
  /** @internal */ readonly [compiled]: true;
}

/**
 * Validate every rule (each pattern through its domain's `canonicalizePattern`,
 * each action against the lattice) and build the internal match index. Throws
 * on an invalid rule — fail closed at authoring time, not decision time.
 */
export function compile(
  domains: readonly PolicyDomain[],
  set: PolicySet,
): CompiledPolicy {
  const domainMap = new Map<string, PolicyDomain>();
  for (const domain of domains) {
    invariant(
      !domainMap.has(domain.id),
      `duplicate policy domain: ${domain.id}`,
    );
    domainMap.set(domain.id, domain);
  }

  const rulesByDomain = new Map<string, CompiledRule[]>();
  for (const rule of set.rules) {
    const domain = domainMap.get(rule.domain);
    invariant(
      domain,
      `rule ${rule.id} names unregistered domain: ${rule.domain}`,
    );
    invariant(
      rule.principals.length > 0,
      `rule ${rule.id} must name at least one principal selector`,
    );

    const patterns = rule.resources.map((raw) => {
      const canonical = domain.canonicalizePattern(raw);
      invariant(
        canonical !== null,
        `rule ${rule.id}: invalid ${rule.domain} pattern: ${JSON.stringify(raw)}`,
      );
      return canonical;
    });

    const bucket = rulesByDomain.get(rule.domain) ?? [];
    bucket.push({
      id: rule.id,
      selectors: rule.principals,
      patterns,
      actionMatch: expandActions(domain, rule.actions),
      effect: rule.effect,
      expiresAt: rule.expiresAt,
      ...(rule.when ? { when: rule.when } : {}),
    });
    rulesByDomain.set(rule.domain, bucket);
  }

  return { domains: domainMap, rulesByDomain } as unknown as CompiledPolicy;
}

/**
 * Decide a request. Pure — `now` (epoch ms) is injected so expiry is
 * deterministic and replayable; when omitted, expiry is not evaluated (a
 * caller enforcing consent-grant decay MUST pass it).
 *
 * Hot path: plain `for` loops, and a deny short-circuits — the chain
 * combination is most-restrictive-wins, so once any principal denies, the
 * decision is a deny no later verdict can change. A policy `Denied` therefore
 * carries provenance up to AND INCLUDING the denying principal (the audit
 * trail of why, not an exhaustive survey).
 */
export function decide(
  policy: CompiledPolicy,
  request: PolicyRequest,
  opts?: { now?: number },
): Decision {
  // Structural gate — short-circuits before any rule sees the request.
  if (request.chain.length === 0) {
    return { effect: "deny", cause: "empty-chain" };
  }
  const domain = policy.domains.get(request.domain);
  if (!domain) {
    return { effect: "deny", cause: "unknown-domain" };
  }
  const canonicalRef = domain.canonicalize(request.ref);
  if (canonicalRef === null) {
    return { effect: "deny", cause: "uncanonical-ref" };
  }

  const rules = policy.rulesByDomain.get(request.domain) ?? [];
  const now = opts?.now;
  const metadata = request.metadata;

  const provenance: PrincipalVerdict[] = [];
  let combined: Effect = "allow";

  for (const principal of request.chain) {
    // Per-principal verdict = most restrictive matched rule; no match ⇒ deny.
    let effect: Effect = "deny";
    let ruleId: string | null = null; // null = default deny (no rule matched)
    let matched = false;

    for (const rule of rules) {
      if (
        rule.expiresAt !== undefined &&
        now !== undefined &&
        rule.expiresAt <= now
      ) {
        continue;
      }
      if (!ruleSpeaksFor(rule, principal, request.groups?.[principal] ?? []))
        continue;
      if (!actionMatches(rule.actionMatch, request.action)) continue;
      if (!patternsMatch(domain, rule.patterns, canonicalRef)) continue;
      // The rule matches by principal/action/resource; its `when` gate decides
      // whether it applies now. A missing or non-equal metadata value means the
      // rule does not apply (it is simply skipped) — see RuleCondition. A
      // caller resolving an async input decides by re-`decide`-ing with the
      // value supplied vs. absent.
      if (rule.when && metadata?.[rule.when.key] !== rule.when.equals) continue;

      if (!matched || RANK[rule.effect] > RANK[effect]) {
        effect = rule.effect;
        ruleId = rule.id;
        matched = true;
        if (effect === "deny") break; // nothing outranks a deny
      }
    }

    provenance.push({ principal, effect, ruleId });
    if (effect === "deny") {
      // Chain combination = intersection; a deny is unbeatable — stop here.
      return { effect: "deny", cause: "policy", canonicalRef, provenance };
    }
    if (RANK[effect] > RANK[combined]) combined = effect;
  }

  if (combined === "allow") {
    return { effect: "allow", canonicalRef, provenance };
  }
  return {
    effect: "escalate",
    canonicalRef,
    provenance,
    grantTemplate: {
      requestedBy: [...request.chain],
      domain: request.domain,
      resources: [canonicalRef],
      actions: [request.action],
    },
  };
}

/**
 * Turn an approved escalation's template into a persistable rule. Pure — the
 * granting act completes when the host persists the result. The template's
 * `resources` are exact canonical refs (they only ever come from `decide`), so
 * every consent-granted rule is canonical by construction.
 *
 * Both remaining decisions are the CALLER's, made explicitly each time:
 * - `principals` — who the approval applies to, a per-consent product choice
 *   ("any member", "this app", one specific actor, …). Deliberately NOT
 *   defaulted from `template.requestedBy`: the requesting chain as
 *   OR-selectors would let one chain member reuse an approval it earned with
 *   a different partner (the multi-principal leak). Use `requestedBy` to
 *   build the choice, not as the answer.
 * - `expiresAt` — every grant explicitly decides its lifetime; `null` is a
 *   deliberate "does not expire", never an accidental default.
 */
export function grant(
  template: GrantTemplate,
  opts: {
    id: string;
    principals: readonly PrincipalSelector[];
    expiresAt: number | null;
  },
): PolicyRule {
  return {
    id: opts.id,
    principals: [...opts.principals],
    domain: template.domain,
    resources: [...template.resources],
    actions: [...template.actions],
    effect: "allow",
    ...(opts.expiresAt !== null ? { expiresAt: opts.expiresAt } : {}),
  };
}

/**
 * Mint a rule id: a provenance-bearing `slot` plus a short content-hash suffix
 * of the rule's SEMANTIC fields (id/description excluded, arrays sorted). Same
 * content → same id → idempotent upserts; changed content → new id. The suffix
 * is informative-for-humans only; nothing should ever parse it back out.
 */
export function ruleId(
  slot: string,
  rule: Omit<PolicyRule, "id" | "description">,
): string {
  return `${slot}-${hashHex(canonicalSerialization(rule))}`;
}

/**
 * The canonical serialization a rule's content hash is computed over — semantic
 * fields only, arrays sorted, so logically-identical rules serialize
 * identically regardless of authoring order. Exported for stores that dedup by
 * content hash in their own columns.
 */
export function canonicalSerialization(
  rule: Omit<PolicyRule, "id" | "description">,
): string {
  return JSON.stringify({
    principals: [...rule.principals].sort(),
    domain: rule.domain,
    resources: [...rule.resources].sort(),
    actions: rule.actions === "*" ? "*" : [...rule.actions].sort(),
    effect: rule.effect,
    expiresAt: rule.expiresAt ?? null,
    // A `when` gate is semantic: a conditioned rule must not share an id with
    // the same rule unconditioned (that would upsert one over the other).
    when: rule.when ? { key: rule.when.key, equals: rule.when.equals } : null,
  });
}

// --- internals -------------------------------------------------------------

/** Does this rule speak for `principal`? Any matching OR-selector suffices:
 *  the principal itself, its kind wildcard, or a group it is in. */
function ruleSpeaksFor(
  rule: CompiledRule,
  principal: Principal,
  groups: readonly GroupSelector[],
): boolean {
  for (const selector of rule.selectors) {
    if (selectorMatches(selector, principal, groups)) return true;
  }
  return false;
}

function patternsMatch(
  domain: PolicyDomain,
  patterns: readonly string[],
  ref: string,
): boolean {
  for (const pattern of patterns) {
    if (domain.matches(pattern, ref)) return true;
  }
  return false;
}

function expandActions(
  domain: PolicyDomain,
  actions: readonly string[] | "*",
): "*" | ReadonlySet<string> {
  if (actions === "*") return "*";
  const out = new Set<string>();
  for (const name of actions) {
    // A name may be a primitive verb (matches itself) or a relation (expands).
    out.add(name);
    const expanded = domain.expandActions?.(name);
    if (expanded) for (const a of expanded) out.add(a);
  }
  return out;
}

function actionMatches(
  match: "*" | ReadonlySet<string>,
  action: string,
): boolean {
  return match === "*" || match.has(action);
}

function selectorMatches(
  selector: PrincipalSelector,
  principal: Principal,
  groups: readonly GroupSelector[],
): boolean {
  if (selector === "*") return true;
  if (selector === principal) return true;
  if (groups.some((group) => group === selector)) return true;
  if (selector === "user:*") return principal.startsWith("user:");
  if (selector === "agent:*") return principal.startsWith("agent:");
  if (selector === "app:*") return principal.startsWith("app:");
  return false;
}

/** FNV-1a → 6 hex chars (24 bits). Uniqueness is the FULL id, so per-slot
 *  collision (same slot, same short hash, different content) is the only risk
 *  and is negligible; a hit merely no-ops a rewrite. Not cryptographic. */
function hashHex(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(6, "0").slice(-6);
}

/** Re-exported for tests/consumers that want the ordering explicit. */
export const EFFECT_BY_RANK: readonly Effect[] = BY_RANK;
